import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Colorful, ColorResult } from '@uiw/react-color';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { hsvaToHexa, hexToHsva } from '@uiw/color-convert';
import { IconBucketDroplet } from '@tabler/icons-react';
import ColorHistory from './ColorHistory';
import { useColorHistoryContext } from '../contexts/ColorHistoryContext';
import { ColourWrapper } from './ToolsPencil';

// Fill tool sub-views
type FILL_VIEW = 'main' | 'color' | 'bg';

export type ToolsFillProps = {
	p5: () => any; // p5 instance
};

const FILL_MODE_HELP =
	'Connected fills only the touching area of the same colour you tap. ' +
	'Whole image replaces that colour everywhere on the canvas. ' +
	'Tip: to fill inside a shape, make sure its outline is fully closed — otherwise the fill spreads out.';

function ToolsFill({ p5 }: ToolsFillProps) {
	const [view, setView] = useState<FILL_VIEW>('main');
	// Fill colour is shared with the pencil colour (changing one changes the other)
	const [hsva, setHsva] = useState({ h: 0, s: 0, v: 68, a: 1 });
	const [bgHsva, setBgHsva] = useState({ h: 0, s: 0, v: 0, a: 1 });
	const [isGlobal, setIsGlobal] = useState(false);

	const { colorHistory, selectColorFromHistory } = useColorHistoryContext();

	useEffect(() => {
		const fillColor = p5()?.getPencilColourHex();
		if (fillColor) setHsva(hexToHsva(fillColor));

		const bgColor = p5()?.getBackgroundColourHex();
		if (bgColor) setBgHsva(hexToHsva(bgColor));

		if (typeof p5()?.getFillGlobal === 'function') setIsGlobal(!!p5().getFillGlobal());
	}, [p5]);

	const updateFillColor = (color: ColorResult) => {
		setHsva(color.hsva);
		p5().setPencilColour(color.hexa); // shared with the pencil
	};

	const handleColorHistorySelect = (color: string) => {
		setHsva(hexToHsva(color));
		p5().setPencilColour(color);
		selectColorFromHistory(color);
	};

	const updateBgColor = (color: ColorResult) => {
		setBgHsva(color.hsva);
		p5().setBackgroundColour(color.hexa);
	};

	const selectFillMode = (isGlobal: boolean) => {
		setIsGlobal(isGlobal);
		p5().setFillGlobal(isGlobal);
	};

	const fillHex = hsvaToHexa(hsva);
	const bgHex = hsvaToHexa(bgHsva);

	return (
		<>
			{view === 'main' && (
				<Box flex={1}>
					<Typography mx={2} mt={1} mb={0} component="h5" fontWeight={700} fontSize={12}>
						Fill
					</Typography>

					{/* Bucket fill */}
					<Box display="flex" alignItems="center" p={1} m={1} mr={0}>
						<Box display="flex" alignItems="center" flex={1} mr={1}>
							<IconWrapper>
								<IconBucketDroplet size={26} color="#e5b800" />
							</IconWrapper>
							<Box ml={2}>
								<Typography fontSize={12} fontWeight={600}>
									Bucket Fill
								</Typography>
								<Typography fontSize={10} color="#aaa">
									Tap an area to fill it
								</Typography>
							</Box>
						</Box>

						<ColorButton onClick={() => setView('color')}>
							<Swatch color={fillHex} />
							Colour
						</ColorButton>
					</Box>

					{/* Fill mode: connected vs whole image */}
					<Box px={2} pb={1}>
						<Box display="flex" alignItems="center" mb={0.5}>
							<Typography fontSize={10} fontWeight={600} color="#aaa" mr={0.5}>
								FILL MODE
							</Typography>
							<Tooltip title={FILL_MODE_HELP} enterTouchDelay={0} leaveTouchDelay={6000} arrow>
								<InfoOutlinedIcon sx={{ fontSize: 14, color: '#888' }} />
							</Tooltip>
						</Box>
						<Segmented>
							<SegmentButton active={!isGlobal} onClick={() => selectFillMode(false)}>
								Connected
							</SegmentButton>
							<SegmentButton active={isGlobal} onClick={() => selectFillMode(true)}>
								Whole image
							</SegmentButton>
						</Segmented>
					</Box>

					<Divider />

					{/* Canvas background colour */}
					<Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1}>
						<Box>
							<Typography fontSize={12} fontWeight={600}>
								Canvas Background
							</Typography>
							<Typography fontSize={10} color="#aaa">
								The colour behind your pixels
							</Typography>
						</Box>
						<ColorButton onClick={() => setView('bg')}>
							<Swatch color={bgHex} />
							Change
						</ColorButton>
					</Box>
				</Box>
			)}

			{view === 'color' && (
				<ColourWrapper>
					<Box display="flex" justifyContent="space-between" alignItems="top">
						<Typography mx={2} mt={1} mb={0} component="h5" fontWeight={700} fontSize={12}>
							Specify Fill Colour
						</Typography>
						<IconButton size="small" onClick={() => setView('main')}>
							<CloseIcon sx={{ color: '#fff' }} />
						</IconButton>
					</Box>

					<Box px={1} pt={1}>
						<ColorHistory colors={colorHistory} onColorSelect={handleColorHistorySelect} currentColor={fillHex} />
					</Box>

					<Colorful style={{ padding: 20, width: '100%' }} color={hsva} onChange={updateFillColor} />
				</ColourWrapper>
			)}

			{view === 'bg' && (
				<ColourWrapper>
					<Box display="flex" justifyContent="space-between" alignItems="top">
						<Typography mx={2} mt={1} mb={0} component="h5" fontWeight={700} fontSize={12}>
							Background Colour
						</Typography>
						<IconButton size="small" onClick={() => setView('main')}>
							<CloseIcon sx={{ color: '#fff' }} />
						</IconButton>
					</Box>

					<Colorful style={{ padding: 20, width: '100%' }} color={bgHsva} onChange={updateBgColor} />
				</ColourWrapper>
			)}
		</>
	);
}

export default ToolsFill;

const IconWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 40px;
	border-radius: 50%;
	border: 1px solid #555;
`;

const ColorButton = styled.button`
	display: flex;
	align-items: center;
	gap: 8px;
	background-color: #444;
	color: white;
	border: none;
	border-radius: 6px;
	padding: 6px 10px;
	font-size: 11px;
	font-weight: 600;
	cursor: pointer;

	&:hover {
		background-color: #555;
	}

	&:active {
		transform: scale(0.97);
	}
`;

const Swatch = styled.div<{ color: string }>`
	width: 18px;
	height: 18px;
	border-radius: 4px;
	border: 1px solid #777;
	background-color: ${props => props.color};
`;

const Segmented = styled.div`
	display: flex;
	width: 100%;
	background-color: #333;
	border-radius: 8px;
	padding: 3px;
	gap: 3px;
`;

const SegmentButton = styled.button.withConfig({
	shouldForwardProp: prop => prop !== 'active',
})<{ active: boolean }>`
	flex: 1;
	padding: 6px 8px;
	border-radius: 6px;
	border: none;
	background-color: ${props => (props.active ? '#e5b800' : 'transparent')};
	color: ${props => (props.active ? '#fff' : '#aaa')};
	font-size: 11px;
	font-weight: ${props => (props.active ? 'bold' : 'normal')};
	cursor: pointer;
	transition: all 0.15s;

	&:hover {
		background-color: ${props => (props.active ? '#e5b800' : '#3d3d3d')};
	}

	&:active {
		transform: scale(0.97);
	}
`;

const Divider = styled.div`
	height: 1px;
	background-color: #383838;
	margin: 4px 12px;
`;
